/**
 * #11212 — when may the album view say "Not available to stream on Qobuz in
 * your region"?
 *
 * Only when a rights signal is actually present. Mycelium's album routes send
 * `rights` (#5958, `qobuz/album-rights.ts`); failing that, returned track rows
 * that are all non-streamable are a present signal too. A failed or empty
 * album fetch (expired Qobuz session, 401, network blip) is missing data, not
 * a region restriction, and must never produce the region verdict. Deryck
 * (MST-6628) got the banner on every album while Qobuz played the same albums.
 */

export interface AlbumRightsLike {
  signal?: string | null;
}

export interface TrackStreamability {
  total: number;
  streamable: number;
}

const PRESENT_UNAVAILABLE_SIGNALS = new Set(['none-streamable', 'future-release']);
const PRESENT_AVAILABLE_SIGNALS = new Set(['available']);

export function isQobuzAlbumUnavailableHere(
  rights: AlbumRightsLike | null | undefined,
  tracks: TrackStreamability,
  albumStreamable?: boolean | null,
): boolean {
  const signal = typeof rights?.signal === 'string' ? rights.signal : null;
  if (signal && PRESENT_UNAVAILABLE_SIGNALS.has(signal)) return true;
  if (signal && PRESENT_AVAILABLE_SIGNALS.has(signal)) return false;
  // No server signal (or 'unknown'): only Qobuz's own flags can carry a verdict —
  // returned rows that are all non-streamable, or an album explicitly marked so.
  if (tracks.total > 0) return tracks.streamable === 0;
  return albumStreamable === false;
}
